import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, BookOpen, FileText, Briefcase, DollarSign,
  Users, Settings, LogOut, Bell, BarChart2, Star, Scale,
  Moon, Sun, PlusCircle, RefreshCw, Receipt, BookMarked, ExternalLink, Share2, UserCheck, Vote, MessageSquare} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { publicAPI } from '../../lib/api';
import { useState, useEffect } from 'react';

const NAV = {
  admin: [
    { icon: LayoutDashboard, label: 'Dashboard', to: '/dashboard' },
    { icon: Users, label: 'Users', to: '/dashboard/users' },
    { icon: BookOpen, label: 'Topics', to: '/dashboard/topics' },
    { icon: FileText, label: 'Applications', to: '/dashboard/applications' },
    { icon: Briefcase, label: 'Projects', to: '/dashboard/projects' },
    { icon: DollarSign, label: 'Sponsorships', to: '/dashboard/sponsorships' },
    { icon: Scale, label: 'Judging', to: '/dashboard/judging' },
    { icon: UserCheck, label: 'Event Check-In', to: '/dashboard/event-checkins' },
    { icon: Star, label: 'Rankings', to: '/dashboard/rankings' },
    { icon: BarChart2, label: 'Analytics', to: '/dashboard/analytics' },
    { icon: Settings, label: 'Program Config', to: '/dashboard/config' },
    { icon: Receipt, label: 'Receipt Verification', to: '/dashboard/receipts' },
    { icon: Share2, label: 'Social Media', to: '/dashboard/social' },
    { icon: RefreshCw, label: 'Season Renewal', to: '/dashboard/renewal' },
    { icon: BookMarked, label: 'User Guides', to: '/dashboard/guide' },
    { icon: Vote, label: 'Live Voting', to: '/dashboard/polls' },
    { icon: MessageSquare, label: 'Feedback', to: '/dashboard/feedback-admin' },
  ],
  lecturer: [
    { icon: LayoutDashboard, label: 'Dashboard', to: '/dashboard' },
    { icon: BookOpen, label: 'Marketplace', to: '/dashboard/marketplace' },
    { icon: BookOpen, label: 'My Topics', to: '/dashboard/my-topics' },
    { icon: Briefcase, label: 'Projects', to: '/dashboard/projects' },
    { icon: BookMarked, label: 'User Guide', to: '/dashboard/guide' },
    { icon: Bell, label: 'Notifications', to: '/dashboard/notifications' },
  ],
  student: [
    { icon: LayoutDashboard, label: 'Dashboard', to: '/dashboard' },
    { icon: BookOpen, label: 'Marketplace', to: '/dashboard/marketplace' },
    { icon: FileText, label: 'My Applications', to: '/dashboard/applications' },
    { icon: PlusCircle, label: 'Post a Topic', to: '/dashboard/student-topics' },
    { icon: Briefcase, label: 'My Projects', to: '/dashboard/projects' },
    { icon: BookMarked, label: 'User Guide', to: '/dashboard/guide' },
    { icon: Bell, label: 'Notifications', to: '/dashboard/notifications' },
  ],
  alumni: [
    { icon: LayoutDashboard, label: 'Dashboard', to: '/dashboard' },
    { icon: BookOpen, label: 'Marketplace', to: '/dashboard/marketplace' },
    { icon: DollarSign, label: 'My Sponsorships', to: '/dashboard/sponsorships' },
    { icon: ExternalLink, label: 'Honour Roll', href: '/honour', external: true },
    { icon: PlusCircle, label: 'Post a Topic', to: '/dashboard/alumni-topics' },
    { icon: Briefcase, label: 'Projects', to: '/dashboard/projects' },
    { icon: BookMarked, label: 'User Guide', to: '/dashboard/guide' },
    { icon: Bell, label: 'Notifications', to: '/dashboard/notifications' },
  ],
  judge: [
    { icon: LayoutDashboard, label: 'Dashboard', to: '/dashboard' },
    { icon: BookOpen, label: 'Marketplace', to: '/dashboard/marketplace' },
    { icon: DollarSign, label: 'My Sponsorships', to: '/dashboard/sponsorships' },
    { icon: Scale, label: 'Assigned Projects', to: '/dashboard/judging' },
    { icon: BookMarked, label: 'User Guide', to: '/dashboard/guide' },
    { icon: Bell, label: 'Notifications', to: '/dashboard/notifications' },
    { icon: ExternalLink, label: 'Honour Roll', href: '/honour', external: true },
  ],
};

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [programName, setProgramName] = useState('Futurum Innovation Awards');
  const [season, setSeason] = useState('Season II');
  const [avatarUrl, setAvatarUrl] = useState(null);

  useEffect(() => {
    publicAPI.getConfig().then(r => {
      const cfg = r.data;
      if (cfg.program_name) setProgramName(cfg.program_name);
      if (cfg.program_season) setSeason(cfg.program_season);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.avatar_url) setAvatarUrl(user.avatar_url);
  }, [user]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const navItems = NAV[user?.role] || [];
  const initials = user ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}` : '?';

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <div className="sidebar-logo-wordmark">{programName}</div>
        <div className="sidebar-logo-sub">{season}</div>
      </div>

      <nav className="sidebar-nav">
        <div className="sidebar-section-label">Navigation</div>
        {navItems.map(item => (
          item.external
            ? <a
                key={item.href}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="sidebar-item"
              >
                <item.icon size={16} className="sidebar-item-icon" />
                {item.label}
              </a>
            : <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/dashboard'}
                className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}
              >
                <item.icon size={16} className="sidebar-item-icon" />
                {item.label}
              </NavLink>
        ))}
      </nav>

      <div className="sidebar-footer">
        {/* Theme toggle */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <button className="theme-toggle" onClick={toggleTheme} title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
            {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
          </button>
        </div>

        <div className="sidebar-user">
          <div className="sidebar-avatar">
            {avatarUrl
              ? <img src={avatarUrl} alt={initials} className="avatar-img" />
              : initials
            }
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sidebar-user-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.first_name} {user?.last_name}
            </div>
            <div className="sidebar-user-role">• {user?.role}</div>
          </div>
          <button
            onClick={handleLogout}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4, flexShrink: 0 }}
            title="Sign out"
          >
            <LogOut size={15} />
          </button>
        </div>
      </div>
    </aside>
  );
}
